import { Patient } from "../../../types/healthcare/PatientTypes";

/**
 * The pure half of `PatientSelect` — the debounced, race-guarded search runner and the option
 * builder.
 *
 * Extracted from the `.tsx` for two reasons. The first is the usual one: the stale-response rule
 * below is the whole point of the component and it deserves a test that cannot be flaky. The
 * second is concrete — **antd v5's `Select` dropdown cannot be opened under jsdom**: its
 * generated stylesheet contains a selector nwsapi rejects
 * (`'*+.ant-select-item-option-selected:not(…)' is not a valid selector`), which surfaces as an
 * uncaught `SyntaxError` during render the moment a search opens the list. So the behaviour is
 * tested here, where there is no DOM at all, rather than through the widget.
 */

export interface PatientSearchRunner {
	/** Debounced. A blank or whitespace-only term issues no request. */
	search(term: string): void;
	/** Cancel the pending debounce and invalidate every in-flight response. */
	dispose(): void;
}

export interface PatientSearchRunnerOptions {
	fetch: (term: string) => Promise<Patient[]>;
	onResults: (patients: Patient[]) => void;
	onSearchingChange: (searching: boolean) => void;
	debounceMs?: number;
}

export const PATIENT_SEARCH_DEBOUNCE_MS = 300;

/**
 * Debounce + **stale-response guard**.
 *
 * ⚠️ The debounce alone is not enough, and assuming it is has burned this codebase before (the
 * commerce order-search S7 regression). A debounce bounds how OFTEN a request is issued; it says
 * nothing about the order the responses come back in. Type "sm", pause past the debounce, type
 * "smith" — two requests are in flight, and if the slow "sm" response lands after the fast
 * "smith" one it silently replaces the correct list with the wrong one, and the operator picks a
 * patient off it.
 *
 * So every search takes a monotonically increasing ticket, and only the LATEST ticket may write:
 * results, and the searching flag. `dispose()` burns a ticket too, so a response arriving after
 * unmount cannot call `setState` on a dead component.
 *
 * A failed lookup deliberately writes NOTHING — blanking the list under an operator who is
 * mid-way through choosing is worse than leaving the previous results up.
 */
export function createPatientSearchRunner(
	options: PatientSearchRunnerOptions,
): PatientSearchRunner {
	const debounceMs = options.debounceMs ?? PATIENT_SEARCH_DEBOUNCE_MS;

	let timer: ReturnType<typeof setTimeout> | null = null;
	let sequence = 0;

	const run = async (term: string) => {
		sequence += 1;
		const ticket = sequence;

		options.onSearchingChange(true);

		try {
			const patients = await options.fetch(term);
			if (ticket !== sequence) {
				return;
			}
			options.onResults(patients ?? []);
		} catch {
			// Intentionally silent: keep whatever is on screen. The request itself is logged by
			// the axios layer; there is nothing actionable to say inside a dropdown.
		} finally {
			if (ticket === sequence) {
				options.onSearchingChange(false);
			}
		}
	};

	return {
		search(term: string) {
			if (timer) {
				clearTimeout(timer);
			}

			const trimmed = (term ?? "").trim();
			if (!trimmed) {
				return;
			}

			timer = setTimeout(() => run(trimmed), debounceMs);
		},

		dispose() {
			if (timer) {
				clearTimeout(timer);
				timer = null;
			}
			sequence += 1;
		},
	};
}

export interface PatientOption {
	value: string;
	label: string;
	title?: string;
	patient?: Patient;
}

/** The display label for a patient. Falls back through name -> email -> id. */
export function patientLabel(patient: Patient): string {
	const name = `${patient.first_name ?? ""} ${patient.last_name ?? ""}`.trim();
	return name || patient.email || `Patient ${patient.id}`;
}

/**
 * Build the option list.
 *
 * Two rows are prepended when they would otherwise be missing, and both exist so that the
 * control's `value` ALWAYS has a matching option — antd renders the raw value as the label when
 * it does not, which on a numeric patient id reads as a bug rather than as a patient:
 *
 *   - the currently `selected` patient, once a later search has pushed them out of the results;
 *   - a bare `Patient <id>` placeholder for a `value` that arrived from outside (editing an
 *     existing invoice, before any search has run).
 */
export function buildPatientOptions(
	results: Patient[],
	selected: Patient | null,
	value?: string,
): PatientOption[] {
	const rows = [...(results ?? [])];

	if (selected && !rows.some((p) => String(p.id) === String(selected.id))) {
		rows.unshift(selected);
	}

	const options: PatientOption[] = rows.map((patient) => ({
		value: String(patient.id),
		label: patientLabel(patient),
		title: patient.email ?? undefined,
		patient,
	}));

	if (value && !options.some((option) => option.value === String(value))) {
		options.unshift({ value: String(value), label: `Patient ${value}` });
	}

	return options;
}
